import clsx from 'clsx';
import React from 'react';

import IconTheWeekend from '@components/Images/Rebranding/IconTheWeekend';
import LazyImgixImage from '@components/LazyImgixImage';
import { weekend_path } from '@routes';
import { getArticleImgSrc } from '@utils/images';

import ArticleDetails from '../ArticleDetails';
import Header from '../Header';
import PictureCaption, { PICTURE_CAPTION_CLASSES } from '../PictureCaption';
import { ThemeViewsProps } from '../interfaces';
import styles from '../styles.module.scss';
import Content from './Content';

const WeekendArticle = ({
  article: {
    addendum,
    advertisement,
    authors,
    commentsCount,
    commenters,
    freeBlurb,
    fullText,
    frontCoverPhoto,
    highlight,
    id,
    pickups,
    picture,
    pictureLink,
    pictureCaption,
    publishedAt,
    scriptList,
    subTitle,
    takeaway,
    title,
  },
  canShareArticle,
  canViewFullArticle,
  canViewComments,
  clipperProps,
  env,
  isSEOBotAndArticleIsNotFree,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  relatedArticles,
  relatedEvent,
  suggestedAiPrompts,
  audioActive,
  setAudioActive,
}: ThemeViewsProps) => {
  const HeroImage = (
    <>
      <LazyImgixImage
        alt={title}
        className="article-hero-image aspect-[10/11] w-full object-cover"
        src={getArticleImgSrc(id, frontCoverPhoto ?? picture, env)}
        visibleByDefault
        wrapperClassName="!hidden aspect-[10/11] max-w-[48vw] lg:-ml-[calc((100vw-1060px)/2)] lg:!flex xl:-ml-[calc((100vw-78.5rem)/2)] xl:max-w-[50vw]"
      />
      <LazyImgixImage
        alt={title}
        className="article-hero-image aspect-[16/9] w-full"
        src={getArticleImgSrc(id, picture, env)}
        visibleByDefault
        wrapperClassName="!flex aspect-[16/9] h-auto w-screen lg:!hidden"
      />
    </>
  );

  return (
    <div className="px-16 sm:px-28">
      <div
        className={clsx('relative m-auto lg:max-w-[1060px] xl:max-w-container-lg', {
          'mb-80 sm:mb-112': !!midArticleUnitType && canViewFullArticle,
        })}
      >
        <div className="mb-24 flex flex-col items-center justify-between sm:mb-40 lg:flex-row lg:gap-x-72 xl:gap-x-64">
          <figure
            className="order-2 m-0 mb-48 sm:mb-56 lg:order-1 lg:mb-0 lg:w-full lg:max-w-[490px] xl:max-w-[633px]"
            aria-labelledby="caption-text"
          >
            {pictureLink ? <a href={pictureLink}>{HeroImage}</a> : HeroImage}
            <PictureCaption
              className="block px-16 sm:px-28 lg:w-full lg:px-0"
              pictureCaption={pictureCaption}
            />
          </figure>
          <div className="relative order-1 mx-auto mb-48 w-full max-w-[270px] text-[1px] sm:mb-64 sm:max-w-[350px] lg:absolute lg:right-0 lg:top-0 lg:max-w-[225px]">
            <a href={weekend_path()}>
              <IconTheWeekend aria-label="The Weekend logo" role="img" />
            </a>
          </div>
          <div className="z-10 order-3 flex-1 lg:order-1 lg:max-w-[560px]">
            <Header
              highlight={highlight}
              title={title}
              titleClassName="sm:!text-headline-lg"
              subTitle={subTitle}
              subTitleClassName="lg:text-subheadline-md xl:text-subheadline-lg"
            />
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row md:gap-72 xl:gap-112">
          <div className="z-10 flex-1">
            <div className="mb-40 sm:mb-48 md:mb-64">
              <ArticleDetails
                authors={authors}
                commentsCount={commentsCount}
                commenters={commenters}
                publishedAt={publishedAt}
                canShareArticle={canShareArticle}
                articleId={id}
                audioActive={audioActive}
                setAudioActive={setAudioActive}
              />
            </div>
          </div>
          <div className={clsx(PICTURE_CAPTION_CLASSES, 'md:w-[195px] md:flex-shrink-0')} />
        </div>
        <Content
          addendum={addendum}
          advertisement={advertisement}
          authors={authors}
          bodyClassName={styles.isFeature}
          canViewFullArticle={canViewFullArticle}
          canViewComments={canViewComments}
          clipperProps={clipperProps}
          commentsCount={commentsCount}
          env={env}
          freeBlurb={freeBlurb}
          fullText={fullText}
          highlight={highlight}
          id={id}
          isSEOBotAndArticleIsNotFree={isSEOBotAndArticleIsNotFree}
          midArticleUnitType={midArticleUnitType}
          mostPopularArticles={mostPopularArticles}
          newsletterId={newsletterId}
          pickups={pickups}
          relatedArticles={relatedArticles}
          relatedEvent={relatedEvent}
          scriptList={scriptList}
          suggestedAiPrompts={suggestedAiPrompts}
          takeaway={takeaway}
          audioActive={audioActive}
        />
      </div>
    </div>
  );
};

export default WeekendArticle;
